import React, { useMemo, useState, useRef, useEffect } from "react";
import { Button } from "src/assets/imports/antdComponents";
import { ITranscriptPreviewPopoverProps } from "../../models/interfaces";
import { useTranslation } from "react-i18next";
import { getOrder } from "../../transformer";
import useIndexedDB from "src/components/useIndexDB";

// Cache the preview image per report page AND per image identity. The S3 object
// path (URL pathname, excluding the rotating presigned query string) distinguishes
// the compressed `cimg` from the full-res image, so a stale full-res blob cached
// under an older key can never be served for the compressed URL the crop expects.
const stableImageKey = (url?: string | null): string => {
	if (!url) return "none";
	try {
		return new URL(url).pathname;
	} catch {
		return url.split("?")[0];
	}
};

const ReportsPreviewPopover: React.FC<ITranscriptPreviewPopoverProps> = ({ references = [], onViewDetails }) => {
	const { t } = useTranslation();
	const [currentIndex, setCurrentIndex] = useState(0);
	const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);
	const [localImageUrl, setLocalImageUrl] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const imageRef = useRef<HTMLImageElement | null>(null);
	const { addOrUpdate, getItem } = useIndexedDB("reportCacheDB", "files");

	const sortedReferences = useMemo(() => {
		if (!references || references.length === 0) return [];

		const hasOrderProperty = references.some((ref) => ref.order !== undefined && ref.order !== null);

		if (!hasOrderProperty) {
			return references;
		}

		return [...references].sort((a, b) => {
			return getOrder(a.order) - getOrder(b.order);
		});
	}, [references]);

	if (sortedReferences.length === 0) return null;

	const currentRef = sortedReferences[currentIndex];
	if (!currentRef) return null;
	const total = sortedReferences.length;

	const handleNext = () => {
		if (currentIndex < total - 1) {
			setCurrentIndex(currentIndex + 1);
		}
	};

	const handlePrev = () => {
		if (currentIndex > 0) {
			setCurrentIndex(currentIndex - 1);
		}
	};

	const handleOpenDrawer = () => {
		onViewDetails?.(currentRef);
	};

	// Use actual bbox from data, with fallback
	const bbox = currentRef.context_preview_image?.bbox || {
		x1: 0,
		y1: 0,
		x2: 100,
		y2: 100,
	};

	const ALLOWED_IMAGE_CONTENT_TYPES = new Set(["image/png", "image/jpeg", "image/jpg", "image/webp"]); // Download presigned URL and convert to local blob URL with CORS handling

	const fetchImageAsBlob = async (url: string): Promise<string> => {
		const cacheKey = `preview-${currentRef?.report_id}-${currentRef?.page_no}-${stableImageKey(url)}`;
		try {
			const response = await fetch(url, {
				method: "GET",
				mode: "cors",
				cache: "no-cache",
				credentials: "omit",
			});

			if (!response.ok) {
				throw new Error(`Failed to fetch image: ${response.status}`);
			} // Content-Type validation (OWASP A04)

			const contentType = response.headers.get("Content-Type")?.split(";")[0]?.trim().toLowerCase();
			if (!contentType || !ALLOWED_IMAGE_CONTENT_TYPES.has(contentType)) {
				throw new Error(`Invalid content type received: ${contentType}. Expected an image type.`);
			}

			const blob = await response.blob();

			const blobUrl = window.URL.createObjectURL(blob); // Try to cache, but don't fail if caching fails

			try {
				await addOrUpdate({ id: cacheKey, fileBlob: blob });
			} catch (cacheError) {
				console.error("Failed to cache image, continuing without cache:", cacheError); // Continue anyway - image is still loaded in memory
			}

			return blobUrl;
		} catch (error) {
			console.error("Error fetching image:", error);
			throw error;
		}
	};

	const getCroppedImg = (image: HTMLImageElement, bbox: any): Promise<string> => {
		const canvas = document.createElement("canvas");

		const cropWidth = bbox.x2 - bbox.x1;
		const cropHeight = bbox.y2 - bbox.y1;

		canvas.width = cropWidth;
		canvas.height = cropHeight;
		const ctx = canvas.getContext("2d");

		if (ctx) {
			ctx.drawImage(image, bbox.x1, bbox.y1, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
		} else {
			console.error("Failed to get canvas context");
			return Promise.reject(new Error("Failed to get canvas context"));
		}

		return new Promise((resolve, reject) => {
			canvas.toBlob(
				(blob) => {
					if (!blob) {
						reject(new Error("Canvas is empty"));
						return;
					}
					const fileUrl = window.URL.createObjectURL(blob);
					resolve(fileUrl);
				},
				"image/jpeg",
				0.95,
			);
		});
	};

	// Handle image load and crop
	const handleImageLoad = async (e: React.SyntheticEvent<HTMLImageElement>) => {
		const image = e.currentTarget;
		imageRef.current = image;

		// Check if bbox exists
		if (!bbox) {
			console.error("bbox is undefined or null");
			setError("Invalid bounding box");
			return;
		}

		if (bbox.x1 === undefined || bbox.x2 === undefined || bbox.y1 === undefined || bbox.y2 === undefined) {
			console.error("Invalid bbox values:", bbox);
			setError("Invalid bounding box values");
			return;
		}

		try {
			const croppedUrl = await getCroppedImg(image, bbox);
			setCroppedImageUrl(croppedUrl);
			setError(null);
		} catch (error) {
			console.error("Error cropping image:", error);
			setError("Failed to crop image");
		}
	};

	// Fetch and convert presigned URL to blob when reference changes
	useEffect(() => {
		const loadImage = async () => {
			if (currentRef.context_preview_image?.image_path) {
				setIsLoading(true);
				setError(null);

				try {
					const cacheKey = `preview-${currentRef?.report_id}-${currentRef?.page_no}-${stableImageKey(currentRef.context_preview_image.image_path)}`;
					const cachedFile = await getItem(cacheKey);

					if (cachedFile !== undefined) {
						const blobUrl = window.URL.createObjectURL(cachedFile.fileBlob);
						setLocalImageUrl(blobUrl);
					} else {
						const blobUrl = await fetchImageAsBlob(currentRef.context_preview_image.image_path);
						setLocalImageUrl(blobUrl);
					}
				} catch (error) {
					console.error("Failed to load image:", error);
					setIsLoading(false);
					setError("Failed to load image");
				} finally {
					setIsLoading(false);
				}
			}
		};

		loadImage();
	}, [currentIndex, currentRef.context_preview_image?.image_path, currentRef?.report_id, currentRef?.entity_id]);

	// Cleanup blob URLs on unmount or when currentIndex changes
	useEffect(() => {
		return () => {
			if (croppedImageUrl) {
				window.URL.revokeObjectURL(croppedImageUrl);
			}
			if (localImageUrl) {
				window.URL.revokeObjectURL(localImageUrl);
			}
		};
	}, [croppedImageUrl, localImageUrl]);

	// Reset cropped image when reference changes
	useEffect(() => {
		setCroppedImageUrl(null);
	}, [currentIndex]);

	return (
		<div className="bg-white shadow-xl rounded-xl" style={{ width: "27.648rem" }}>
			{/* Sticky Header */}
			<div className={`flex items-center justify-between px-4 sticky top-0 bg-white z-10 ${total === 1 ? "pt-4" : "pt-3"}`}>
				<div className="flex-1 min-w-0 pr-3">
					<span className="text-sm font-semibold block truncate text-[--secondary-color]">{currentRef.file_name}</span>
				</div>

				{/* Navigation arrows */}
				{total > 1 && (
					<div className="flex items-center gap-1.5 flex-shrink-0">
						<button
							onClick={handlePrev}
							disabled={currentIndex === 0}
							className={`w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors
                ${currentIndex === 0 ? "opacity-30 cursor-not-allowed" : "cursor-pointer"}
              `}
							style={{ color: "var(--secondary-color)" }}>
							<i className="material-symbols-outlined text-base font-bold">chevron_backward</i>
						</button>
						<span className="text-sm font-semibold whitespace-nowrap px-1">
							<span style={{ color: "var(--secondary-color)" }}>{currentIndex + 1}</span>
							<span style={{ color: "#B1B1B5" }}>/{total}</span>
						</span>
						<button
							onClick={handleNext}
							disabled={currentIndex === total - 1}
							className={`w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors
                ${currentIndex === total - 1 ? "opacity-30 cursor-not-allowed" : "cursor-pointer"}
              `}
							style={{ color: "var(--secondary-color)" }}>
							<i className="material-symbols-outlined text-base font-bold">chevron_forward</i>
						</button>
					</div>
				)}
			</div>

			{/* Preview Content */}
			<div className="px-4 pt-3 pb-2">
				<div className="p-2 bg-[--secondary-color-opacity-5] rounded-t-lg">
					{/* Text Preview */}
					<div className="text-sm text-[--secondary-color] leading-relaxed">
						{typeof currentRef.context_preview === "string"
							? currentRef.context_preview || t("No preview available")
							: currentRef.context_preview?.text || t("No preview available")}
					</div>
				</div>

				{/* Loading state */}
				{isLoading && <div className="mb-3 p-4 py-[5.2rem] text-center text-gray-500 border border-gray-200 rounded-b-lg">Loading image...</div>}

				{/* Error state */}
				{error && <div className="mb-3 p-4 py-[5.2rem] text-center text-red-500 border border-red-200 rounded-b-lg">{error}</div>}

				{/* Hidden original image for cropping - using local blob URL */}
				{localImageUrl && bbox && !error && (
					<img
						ref={imageRef}
						src={localImageUrl}
						alt="Original"
						style={{ display: "none" }}
						onLoad={handleImageLoad}
						onError={(e) => {
							console.error("Image failed to load:", e);
							setError("Image failed to load");
						}}
					/>
				)}

				{/* Display cropped image that fits in the box */}
				{croppedImageUrl && !error && (
					<div
						className="mb-3 border-x border-b border-gray-200 rounded-b-lg overflow-hidden bg-gray-50 flex items-center justify-center"
						style={{
							width: "100%",
							height: "150px",
						}}>
							<img
								src={croppedImageUrl}
								alt="Cropped preview"
								style={{
									width: "100%",
									maxHeight: "100%",
								}}
							/>
					</div>
				)}

				{/* Footer with Study name and View Details */}
				<div className="flex justify-between items-center pt-2 pb-1">
					<span className="text-xs font-medium underline" style={{ color: "var(--secondary-color)" }}>
						{currentRef.entity_name ? `${currentRef.entity_name}` : ""}
					</span>
					<Button type="link" onClick={handleOpenDrawer} className="font-semibold hover transition-colors p-0 h-auto text-sm" style={{ color: "var(--primary-color)" }}>
						{t("View Details")}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default ReportsPreviewPopover;
